import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as ClockIcon } from '@repo/common/assets/icons/20px/icon_20px_clock_outline.svg';
import { cn } from './model/derive';

/**
 * 시각 입력 (HH:mm) — 작업 시간 구간 전용.
 *   · 글자로 바로 고칠 수 있다 (숫자만 쳐도 : 이 자동으로 들어간다 — 1015 → 10:15)
 *   · 시계 아이콘 → 10분 단위 목록에서 고른다
 *   · Enter 나 칸을 떠나면 마무리한다 — 덜 친 값은 채워 준다 (9 → 09:00, 930 → 09:30)
 * 값은 'HH:mm'. 틀린 글자는 마무리할 때 원래 값으로 돌아간다.
 * limit(t) 를 주면 고른 · 친 시각을 그 값으로 바꿔 넣고, 목록에서 바뀌는 시각은 고를 수 없게 한다.
 */

const pad = (n) => String(n).padStart(2, '0');
const STEP = 10;
const LIST = Array.from(
  { length: (24 * 60) / STEP },
  (_, i) => `${pad(Math.floor((i * STEP) / 60))}:${pad((i * STEP) % 60)}`,
);
const POP = { w: 120, h: 250 };

/** 숫자만 뽑아 'HH:mm' 모양으로 */
const mask = (raw) => {
  const d = raw.replace(/\D/g, '').slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d;
};

/** 덜 친 시각을 'HH:mm' 으로 — 9 → 09:00, 930 → 09:30, 1015 → 10:15. 안 되면 null */
const complete = (raw) => {
  const d = raw.replace(/\D/g, '');
  if (!d) return null;
  const [h, m] =
    d.length <= 2
      ? [Number(d), 0]
      : d.length === 3
        ? [Number(d[0]), Number(d.slice(1))]
        : [Number(d.slice(0, 2)), Number(d.slice(2, 4))];
  return h <= 23 && m <= 59 ? `${pad(h)}:${pad(m)}` : null;
};

/** 'HH:mm' 이 있는 시각이면 그대로, 아니면 null */
export const parseTime = (text) => {
  const m = /^(\d{2}):(\d{2})$/.exec(text);
  return m && Number(m[1]) <= 23 && Number(m[2]) <= 59 ? text : null;
};

const TimeField = ({
  value,
  onChange,
  disabled,
  invalid,
  className,
  limit = (t) => t,
  'aria-label': ariaLabel,
}) => {
  const [text, setText] = useState(value ?? '');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrap = useRef(null);
  const list = useRef(null);
  const input = useRef(null);

  // 시각을 넣는다 — limit 에 걸리면 바뀐 값으로
  const put = (t) => {
    const v = limit(t);
    setText(v);
    onChange(v);
  };
  // Enter · 칸을 떠날 때 — 덜 친 값은 채우고, 안 되는 글자는 원래 값으로
  const finish = () => {
    const t = complete(text);
    if (t) put(t);
    else setText(value ?? '');
  };

  // 바깥에서 값이 바뀌면 (구간 추가 · 초기화 등) 글자도 맞춘다
  useEffect(() => {
    if (value !== parseTime(text)) setText(value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 목록은 잘리지 않게 화면 위에 띄운다 — 아래 자리가 모자라면 위로 연다
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = wrap.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const top =
        below < POP.h + 8 && r.top > below ? Math.max(8, r.top - POP.h - 4) : r.bottom + 4;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - POP.w - 8));
      setPos({ top, left });
    };
    // 목록 자체를 스크롤할 때는 자리를 다시 잡지 않는다 (다시 잡으면 지금 값으로 되돌아가 스크롤이 안 됐다)
    const onScroll = (e) => {
      if (!list.current?.contains(e.target)) place();
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // 바깥을 누르거나 Escape 면 닫는다
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!wrap.current?.contains(e.target) && !list.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // 목록을 열 때 한 번만 지금 값(없으면 가장 가까운 10분)이 가운데 오게 — 목록 안에서만 움직인다
  const ready = Boolean(open && pos);
  useEffect(() => {
    const box = list.current;
    if (!ready || !box) return;
    const el =
      box.querySelector('[aria-selected="true"]') ??
      box.querySelector(`[data-time="${(value ?? '').slice(0, 4)}0"]`);
    if (el) box.scrollTop = el.offsetTop - (box.clientHeight - el.offsetHeight) / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const bad = text.length === 5 && !parseTime(text);

  return (
    <div ref={wrap} className={cn('relative', className)}>
      <div
        className={cn(
          'flex h-full min-h-10 items-center rounded-lg border bg-grayscale-gray00 pl-3 pr-1 focus-within:border-green-deep02',
          invalid || bad ? 'border-functional-failed03' : 'border-green-deep03/20',
          disabled && 'bg-grayscale-gray01 text-text-disable',
        )}
      >
        <input
          ref={input}
          value={text}
          disabled={disabled}
          inputMode='numeric'
          autoComplete='off'
          placeholder='HH:mm'
          aria-label={ariaLabel}
          aria-invalid={invalid || bad || undefined}
          onChange={(e) => {
            const next = mask(e.target.value);
            setText(next);
            const ok = parseTime(next);
            if (ok) put(ok);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            // 폼 제출 대신 이 칸만 마무리한다 (떠나면서 onBlur 가 finish 를 부른다)
            e.preventDefault();
            setOpen(false);
            input.current?.blur();
          }}
          onBlur={finish}
          className='h-full w-full min-w-0 flex-1 bg-transparent text-[14px] tabular-nums text-text-primary placeholder:text-text-placeholder focus:outline-none disabled:text-text-disable'
        />
        {!disabled && (
          <button
            type='button'
            aria-label={`${ariaLabel ?? 'Time'} · pick from list`}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-icon-secondary hover:bg-grayscale-gray01 hover:text-text-primary'
          >
            <ClockIcon className='h-5 w-5' />
          </button>
        )}
      </div>

      {open &&
        pos &&
        ReactDOM.createPortal(
          <ul
            ref={list}
            role='listbox'
            aria-label={ariaLabel ?? 'Times'}
            style={{ ...pos, width: POP.w }}
            className='fixed z-[1300] max-h-[240px] overflow-y-auto rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-1 shadow-popup-01'
          >
            {LIST.map((t) => (
              <li key={t}>
                <button
                  type='button'
                  role='option'
                  data-time={t}
                  aria-selected={t === value}
                  disabled={limit(t) !== t}
                  onClick={() => {
                    put(t);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex h-9 w-full items-center rounded-lg px-3 text-[14px] tabular-nums disabled:cursor-not-allowed disabled:text-text-disable disabled:hover:bg-transparent',
                    t === value
                      ? 'bg-surface-green10 font-medium text-text-primary'
                      : 'text-text-primary hover:bg-grayscale-gray01',
                  )}
                >
                  {t}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default TimeField;
